import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { idFromSlug, recipePath, cookbookPath } from "@/lib/slug";
import api from "@/lib/api";
import { toastTrashed } from "@/lib/trashToast";
import { toast } from "sonner";
import { Trash, FloppyDisk, UploadSimple, X } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import AutoGrowTextarea from "@/components/AutoGrowTextarea";
import SortableList from "@/components/SortableList";
import CopyNeedsImageDialog from "@/components/CopyNeedsImageDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function RecipeEdit() {
  const { id: slugId } = useParams();
  const id = idFromSlug(slugId);
  const nav = useNavigate();
  const { user } = useAuth();
  // AI headline image is a SuperAdmin-only internal tool used to backfill
  // hero images on imported / OCR'd recipes. Hidden entirely for everyone
  // else so it never surfaces as a user-facing feature.
  const isSuperAdmin = user?.role === "SuperAdmin";
  // Admin override: /r/:slugId/edit?admin=1 routes the PATCH/DELETE through
  // the admin endpoints, which let staff edit recipes they don't own. The UI
  // shows an "Admin mode" banner so the operator knows what they're editing.
  const [searchParams] = useSearchParams();
  const adminMode = searchParams.get("admin") === "1";
  // `return_to` lets the caller (typically a cookbook page whose "Edit" button
  // opened this recipe) ask us to redirect there after save/delete instead of
  // to the recipe view or dashboard.
  const returnTo = searchParams.get("return_to");
  const [data, setData] = useState(null);
  const [cookbook, setCookbook] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [suggestingTags, setSuggestingTags] = useState(false);
  const [generatingAiImage, setGeneratingAiImage] = useState(false);
  const [deletingAiImage, setDeletingAiImage] = useState(false);
  const fileRef = useRef(null);
  const initialJson = useRef(null);
  const [needImageOpen, setNeedImageOpen] = useState(false);
  // Stable row ids for ingredient / instruction lists — required for @dnd-kit
  // to keep the dragged row identifiable across renders AND to preserve input
  // focus during reorder. Ids are LOCAL ONLY; never persisted or sent to API.
  const ridCounter = useRef(0);
  const [ingIds, setIngIds] = useState([]);
  const [stepIds, setStepIds] = useState([]);
  const newRid = (prefix) => `${prefix}-${ridCounter.current++}`;

  const generateAiImage = async (keep = false) => {
    setGeneratingAiImage(true);
    try {
      // Kicks off the job — returns immediately with status "generating".
      // We poll the status endpoint every 3s until it flips to "ready" or
      // "failed". This keeps client-side wait times unbounded without ever
      // exceeding Cloudflare's 100s HTTP edge timeout.
      const { data: kick } = await api.post(`/recipes/${id}/ai-image`);
      if (kick.ai_image_status !== "generating" && kick.ai_image_url) {
        // Legacy synchronous response — apply directly
        setData((d) => ({
          ...d,
          ai_image_url: kick.ai_image_url,
          ai_image_generated_at: new Date().toISOString(),
          ai_image_active: keep,
        }));
        toast.success("AI image ready — Keep, Replace, or Delete below.");
        return;
      }

      // Poll status every 3 seconds up to ~5 minutes.
      const maxAttempts = 100;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: s } = await api.get(`/recipes/${id}/ai-image/status`);
        if (s.ai_image_status === "ready" && s.ai_image_url) {
          setData((d) => ({
            ...d,
            ai_image_url: s.ai_image_url,
            ai_image_generated_at: s.ai_image_generated_at || new Date().toISOString(),
            ai_image_active: keep,
          }));
          toast.success(keep ? "AI image ready and set as the main image — tap Save to publish." : "AI image ready — Keep, Replace, or Delete below.");
          return;
        }
        if (s.ai_image_status === "failed") {
          throw new Error(s.ai_image_error || "AI image generation failed. Try again in a minute.");
        }
        // else still "generating" — keep polling
      }
      throw new Error("AI image is taking longer than expected. Please refresh in a minute.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "AI image generation failed. Try again in a minute.");
    } finally {
      setGeneratingAiImage(false);
    }
  };

  const deleteAiImage = async () => {
    setDeletingAiImage(true);
    try {
      await api.delete(`/recipes/${id}/ai-image`);
      setData((d) => ({
        ...d,
        ai_image_url: null,
        ai_image_generated_at: null,
        ai_image_active: false,
      }));
      toast.success("AI image deleted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    } finally {
      setDeletingAiImage(false);
    }
  };

  useEffect(() => {
    api.get(`/recipes/${id}`).then((r) => {
      setData(r.data);
      initialJson.current = JSON.stringify(r.data);
      // Assign stable row-ids so drag-reorder never loses input focus.
      // Ids live only in local state — never persisted or sent to the API.
      const startIng = ridCounter.current;
      setIngIds((r.data.ingredients || []).map((_, i) => `ing-${startIng + i}`));
      ridCounter.current += (r.data.ingredients || []).length;
      const startStep = ridCounter.current;
      setStepIds((r.data.instructions || []).map((_, i) => `step-${startStep + i}`));
      ridCounter.current += (r.data.instructions || []).length;
      api.get(`/cookbooks/${r.data.cookbook_id}`).then((c) => setCookbook(c.data)).catch(() => {});
    }).catch(() => toast.error("Not found"));
  }, [id]);

  // Permission gate: only the recipe owner, the parent cookbook's co-editors,
  // or admin staff (Admin / SuperAdmin) may edit. Anyone else who lands here
  // (typically by URL-typing after browsing the public Directory) is bounced
  // back to the read-only recipe page with a toast. Backend PATCH still 403s
  // as defence-in-depth.
  useEffect(() => {
    if (!data || !cookbook || !user) return;
    const isOwner = data.owner_id === user.user_id;
    const isCoEditor = Array.isArray(cookbook.co_editor_ids) && cookbook.co_editor_ids.includes(user.user_id);
    const isStaff = user.role === "SuperAdmin" || user.role === "Admin";
    if (!isOwner && !isCoEditor && !isStaff) {
      toast.error("You can only edit recipes you own or have been invited to co-edit.");
      nav(recipePath(data), { replace: true });
    }
  }, [data, cookbook, user, nav]);

  const [tagIdeas, setTagIdeas] = useState([]);
  const ideaKey = data ? `${data.title || ""}|${(data.ingredients || []).map((i) => i?.name || "").join(",")}` : "";
  useEffect(() => {
    if (!ideaKey || ideaKey === "|") return undefined;
    const t = setTimeout(() => {
      api.post("/tags/suggest", { title: data.title, ingredients: (data.ingredients || []).map((i) => i?.name || ""), tags: data.tags || [] })
        .then(({ data: d }) => setTagIdeas(d.suggestions || []))
        .catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [ideaKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <div className="p-10 text-center">Loading…</div>;
  const isCopyOwner = !!data.copied_from_recipe_id && data.owner_id === user?.user_id;
  const uneditedCopyLocked = data.copy_state === "unedited" && JSON.stringify(data) === initialJson.current;

  // Functional setState so async callbacks (AI image generate, upload, etc.)
  // don't clobber each other with stale `data` snapshots. Previously three
  // consecutive `set()` calls after an `await` would all read the same stale
  // `data`, so only the last write persisted — that's why the AI image URL
  // never appeared even though the request succeeded.
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const uploadImages = async (files) => {
    if (!files?.length) return;
    const orig = new Set(data.copied_image_urls || []);
    if ((data.images || []).filter((u) => !orig.has(u)).length + files.length > 6) {
      toast.error("Up to 6 images per recipe.");
      return;
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const form = new FormData();
        form.append("file", f);
        const { data: res } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(res.url);
      }
      set("images", [...(data.images || []), ...uploaded]);
      toast.success("Photos added");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    try {
      // Route through the admin endpoint automatically whenever the
      // current user is an Admin/SuperAdmin AND isn't the recipe owner.
      // This is what makes "Super Admin can edit any recipe on the site"
      // actually work — previously we only used the admin path when
      // ?admin=1 was in the URL, so editing from the Directory silently
      // 403'd because /recipes/{id} rejected non-owner PATCH.
      const isOwnerNow = user?.user_id === data.owner_id;
      const isStaff = user?.role === "SuperAdmin" || user?.role === "Admin";
      const useAdmin = adminMode || (isStaff && !isOwnerNow);
      const url = useAdmin ? `/admin/recipes/${id}` : `/recipes/${id}`;
      await api.patch(url, {
        title: data.title, creator_name: data.creator_name, creation_year: data.creation_year, cuisine: (data.cuisine || "").trim() || null,
        serves: data.serves, prep_minutes: data.prep_minutes, cook_minutes: data.cook_minutes,
        rest_minutes: data.rest_minutes,
        difficulty: data.difficulty, ingredients: data.ingredients, instructions: data.instructions,
        is_public: data.is_public, is_secret: data.is_secret, copyright_certified: data.copyright_certified,
        tools: data.tools, experiment_level: data.experiment_level, images: data.images,
        tags: data.tags || [],
        // AI headline image "use as main image" toggle. Without this the
        // "Keep" button + the checkbox both lived only in local state and
        // silently dropped on save, so the recipe hero kept showing the
        // branded default even after an admin promoted the AI image.
        ai_image_active: !!data.ai_image_active,
        show_nutrition: data.show_nutrition === true,
      });
      toast.success("Saved");
      initialJson.current = JSON.stringify(data);
      sessionStorage.setItem(`rf_fresh_${id}`, "1");
      // Priority: explicit ?return_to param → whatever the caller asked for.
      // Otherwise fall back to the pre-existing behaviour.
      if (returnTo) {
        nav(returnTo);
      } else {
        nav(useAdmin && !adminMode ? recipePath(data) : (adminMode ? "/admin" : recipePath(data)));
      }
    } catch (e) {
      if (e?.response?.status === 409) { setNeedImageOpen(true); return; }
      // Surface the actual backend reason so users can act on it instead
      // of the useless "Save failed" that hid every 403 / validation
      // error before. The 422-flattening interceptor in lib/api.js turns
      // Pydantic error arrays into a readable "field: message" string.
      const reason = e?.response?.data?.detail || e?.message || "Unknown error";
      toast.error(`Save failed: ${reason}`, { duration: 8000 });
    }
  };

  // Mobile-friendly delete: uses shadcn AlertDialog instead of window.confirm.
  // window.confirm is silently blocked in some mobile Safari contexts (PWA / iOS
  // Add-to-Home-Screen mode), which is why the Delete button "did nothing" on
  // some phones. AlertDialog is a proper DOM modal that always renders.
  const doDelete = async () => {
    const isOwnerNow = user?.user_id === data.owner_id;
    const isStaff = user?.role === "SuperAdmin" || user?.role === "Admin";
    const useAdmin = adminMode || (isStaff && !isOwnerNow);
    const url = useAdmin ? `/admin/recipes/${id}` : `/recipes/${id}`;
    try {
      await api.delete(url);
      if (useAdmin) toast.success("Deleted");
      else toastTrashed([id], { message: "Moved to Trash", onUndone: () => nav(recipePath(data)) });
      // If the user got here via a cookbook page, honour that return path
      // even after deletion — the cookbook still exists.
      nav(returnTo || (adminMode ? "/admin" : "/dashboard"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const updIng = (i, k, v) => { const a = [...data.ingredients]; a[i] = { ...a[i], [k]: v }; set("ingredients", a); };
  const updStep = (i, v) => { const a = [...data.instructions]; a[i] = v; set("instructions", a); };

  // ---------- Tag editor ----------
  // Case-insensitive dedupe so "Vegan" and "vegan" don't both end up in the list.
  const addTag = (raw) => {
    const t = (raw || "").trim().replace(/^#/, "").slice(0, 40);
    if (!t) return;
    const existing = data.tags || [];
    if (existing.some((x) => x.toLowerCase() === t.toLowerCase())) {
      toast.info(`"${t}" is already tagged`);
      return;
    }
    set("tags", [...existing, t]);
  };
  const removeTag = (t) => set("tags", (data.tags || []).filter((x) => x !== t));

  // AI suggests the 4 canonical tags (meal type · main ingredient · dietary · cuisine)
  // and merges them with any user-added tags. Preserves existing tags.
  const suggestTags = async () => {
    setSuggestingTags(true);
    try {
      const { data: res } = await api.post(`/recipes/${id}/auto-tag`);
      set("tags", res.tags || []);
      if (res.added?.length) toast.success(`Added ${res.added.length} tag${res.added.length === 1 ? "" : "s"}: ${res.added.join(", ")}`);
      else toast.info("Already tagged — no new suggestions");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI tagging failed");
    } finally {
      setSuggestingTags(false);
    }
  };

  const hardDelete = adminMode || (["SuperAdmin", "Admin"].includes(user?.role) && user?.user_id !== data.owner_id);

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-12">
      <CopyNeedsImageDialog
        open={needImageOpen}
        onOpenChange={setNeedImageOpen}
        generating={generatingAiImage}
        onUpload={() => { setNeedImageOpen(false); fileRef.current?.click(); }}
        onGenerate={async () => { await generateAiImage(true); setNeedImageOpen(false); }}
      />
      <Link to={adminMode ? "/admin" : recipePath(data)} className="text-sm font-bold text-rf-orange hover:underline mb-6 inline-block">
        {adminMode ? "← Back to Admin" : "← Back to recipe"}
      </Link>
      {adminMode && (
        <div className="mb-6 rounded-2xl bg-red-50 border-2 border-red-300 px-4 py-3 flex items-center gap-3" data-testid="admin-edit-banner">
          <span className="text-2xl">⚠️</span>
          <div>
            <p className="font-display font-extrabold text-red-800 text-sm">Admin override</p>
            <p className="text-xs text-red-700">You&apos;re editing someone else&apos;s recipe. Changes are logged. Owner: <b>{data.creator_name || "—"}</b></p>
          </div>
        </div>
      )}
      <p className="overline mb-3">Edit recipe</p>
      <h1 className="font-display text-4xl md:text-5xl font-black text-rf-ink mb-8 leading-[0.95]">Polish to perfection.</h1>

      <div className="space-y-5">
        <div>
          <label className="overline">Title</label>
          <input data-testid="edit-title" value={data.title} onChange={(e) => set("title", e.target.value)} className="input-rf mt-1 !text-2xl !font-display !font-black" />
        </div>
        <div>
          <label className="overline" htmlFor="edit-cuisine">Cuisine</label>
          <input id="edit-cuisine" data-testid="edit-cuisine" value={data.cuisine || ""} maxLength={40} list="edit-cuisine-options"
                 onChange={(e) => set("cuisine", e.target.value)} placeholder="e.g. Italian, Indian, British" className="input-rf mt-1" />
          <datalist id="edit-cuisine-options">
            {["British", "Irish", "French", "Italian", "Spanish", "Greek", "Indian", "Chinese", "Japanese", "Thai", "Mexican", "American", "Middle Eastern", "Mediterranean", "Caribbean", "African"].map((c) => <option key={c} value={c} />)}
          </datalist>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div><label className="overline">Serves</label>
            <input
              type="number" inputMode="numeric" min="0"
              value={data.serves ?? ""}
              onChange={(e) => set("serves", e.target.value === "" ? null : Math.max(0, +e.target.value))}
              onWheel={(e) => e.target.blur()}
              className="input-rf mt-1"
            />
          </div>
          <div><label className="overline">Prep (min)</label>
            <input
              type="number" inputMode="numeric" min="0"
              value={data.prep_minutes ?? ""}
              onChange={(e) => set("prep_minutes", e.target.value === "" ? null : Math.max(0, +e.target.value))}
              onWheel={(e) => e.target.blur()}
              placeholder="—"
              className="input-rf mt-1"
            />
          </div>
          <div><label className="overline">Cook (min)</label>
            <input
              type="number" inputMode="numeric" min="0"
              value={data.cook_minutes ?? ""}
              onChange={(e) => set("cook_minutes", e.target.value === "" ? null : Math.max(0, +e.target.value))}
              onWheel={(e) => e.target.blur()}
              placeholder="—"
              className="input-rf mt-1"
            />
          </div>
          <div><label className="overline">Rest (min)</label>
            <input
              type="number" inputMode="numeric" min="0"
              value={data.rest_minutes ?? ""}
              onChange={(e) => set("rest_minutes", e.target.value === "" ? null : Math.max(0, +e.target.value))}
              onWheel={(e) => e.target.blur()}
              placeholder="—"
              className="input-rf mt-1"
              title="Rest / chill / cool / marinate time — auto-detected from the method"
            />
          </div>
          <div>
            <label className="overline">Difficulty</label>
            <select value={data.difficulty} onChange={(e) => set("difficulty", e.target.value)} className="input-rf mt-1">
              <option>Easy</option><option>Medium</option><option>Hard</option>
            </select>
          </div>
        </div>
        {/* Recompute times helper — scans the method for "simmer 10 min /
            rest 30 min" phrases and refreshes the Prep / Cook / Rest boxes
            above. Only overwrites empty fields, so user edits are safe. */}
        <div>
          <button
            type="button"
            data-testid="edit-recompute-times"
            onClick={async () => {
              try {
                const { data: t } = await api.post("/recipes/compute-times", { instructions: data.instructions });
                setData((d) => ({
                  ...d,
                  prep_minutes: d.prep_minutes ?? t.prep_minutes ?? null,
                  cook_minutes: d.cook_minutes ?? t.cook_minutes ?? null,
                  rest_minutes: d.rest_minutes ?? t.rest_minutes ?? null,
                }));
                const bits = [];
                if (t.prep_minutes) bits.push(`prep ${t.prep_minutes}m`);
                if (t.cook_minutes) bits.push(`cook ${t.cook_minutes}m`);
                if (t.rest_minutes) bits.push(`rest ${t.rest_minutes}m`);
                toast.success(bits.length ? `Detected ${bits.join(" · ")}` : "No time phrases found in the method");
              } catch { toast.error("Couldn't recompute times"); }
            }}
            className="text-xs font-bold text-rf-orange hover:underline inline-flex items-center gap-1"
          >
            ↻ Recompute prep / cook / rest from the method
          </button>
        </div>

        <div>
          <label className="overline">Creator</label>
          <input value={data.creator_name || ""} onChange={(e) => set("creator_name", e.target.value)} className="input-rf mt-1" />
        </div>

        {/* Tags — free-form editor + AI suggestions.
            Tags help this recipe surface in the cookbook filters AND in the
            public directory once the recipe is made public. */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="overline">Tags</label>
            <button
              type="button"
              data-testid="edit-tags-suggest"
              onClick={suggestTags}
              disabled={suggestingTags}
              className="text-rf-orange text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50"
            >
              <i className="fa-solid fa-wand-magic-sparkles" /> {suggestingTags ? "Thinking…" : "AI suggest tags"}
            </button>
          </div>
          <p className="text-xs text-rf-ink-soft mb-3">
            Add tags so this recipe can be found later — meal type, main ingredient, diet
            (meat / vegetarian / vegan / pescatarian) and cuisine. Public recipes use tags
            in the Directory filters too.
          </p>
          {(data.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3" data-testid="edit-tag-chips">
              {data.tags.map((t) => (
                <span
                  key={t}
                  data-testid={`edit-tag-chip-${t}`}
                  className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-rf-amber text-rf-ink text-xs font-bold"
                >
                  {t}
                  <button
                    type="button"
                    onClick={() => removeTag(t)}
                    data-testid={`edit-tag-remove-${t}`}
                    className="w-5 h-5 rounded-full bg-rf-ink/10 hover:bg-rf-ink/25 flex items-center justify-center leading-none"
                    aria-label={`Remove tag ${t}`}
                  >
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              data-testid="edit-tag-input"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                // Enter OR comma commits the tag. Backspace on empty input removes the last one.
                if (e.key === "Enter" || e.key === ",") {
                  e.preventDefault();
                  addTag(tagInput);
                  setTagInput("");
                } else if (e.key === "Backspace" && !tagInput && (data.tags || []).length) {
                  removeTag(data.tags[data.tags.length - 1]);
                }
              }}
              placeholder="Type a tag and press Enter (e.g. Dinner, Chicken, Italian)"
              className="input-rf flex-1"
            />
            <button
              type="button"
              data-testid="edit-tag-add"
              onClick={() => { addTag(tagInput); setTagInput(""); }}
              className="btn-outline-ink !px-4"
            >Add</button>
          </div>
          {tagIdeas.filter((t) => !(data.tags || []).some((x) => x.toLowerCase() === t.toLowerCase())).length > 0 && (
            <div className="mt-4" data-testid="edit-tag-ideas">
              <p className="text-xs text-rf-ink-soft mb-2">Suggested from the title and ingredients — tap to add:</p>
              <div className="flex flex-wrap gap-2">
                {tagIdeas
                  .filter((t) => !(data.tags || []).some((x) => x.toLowerCase() === t.toLowerCase()))
                  .map((t) => (
                    <button type="button" key={t} data-testid={`edit-tag-idea-${t}`} onClick={() => addTag(t)}
                      className="px-3 py-1.5 rounded-full text-xs font-bold bg-rf-amber/20 text-rf-ink hover:bg-rf-amber/40 transition-colors">+ {t}</button>
                  ))}
              </div>
            </div>
          )}
          {cookbook?.categories?.length > 0 && (
            <div className="mt-4">
              <p className="text-xs text-rf-ink-soft mb-2">Quick add from <b>{cookbook.title}</b> categories:</p>
              <div className="flex flex-wrap gap-2">
                {cookbook.categories
                  .filter((c) => !(data.tags || []).some((t) => t.toLowerCase() === c.toLowerCase()))
                  .map((c) => (
                    <button
                      type="button"
                      key={c}
                      data-testid={`edit-tag-suggest-${c}`}
                      onClick={() => addTag(c)}
                      className="px-3 py-1.5 rounded-full text-xs font-bold bg-rf-cream-alt text-rf-ink-soft hover:bg-rf-amber/30 transition-all"
                    >+ {c}</button>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* Image upload */}
        <div>
          <label className="overline">Recipe photos (up to 6)</label>
          {/* Prominent full-width upload zone — the previous tiny "Add photo"
              text link was easy to miss (multiple user complaints Aug 2026).
              Now a proper dashed dropzone with big icon + label. */}
          {(data.images?.length || 0) < 6 && (
            <button
              type="button"
              data-testid="edit-add-photo"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              className="mt-2 w-full rounded-2xl border-2 border-dashed border-rf-orange/50 bg-rf-amber/10 hover:bg-rf-amber/20 hover:border-rf-orange transition-colors disabled:opacity-60 disabled:cursor-wait flex flex-col items-center justify-center gap-2 py-8 px-4"
            >
              <UploadSimple size={32} weight="duotone" className="text-rf-orange" />
              <span className="font-display font-black text-lg text-rf-ink">
                {uploading ? "Uploading…" : "Add photo"}
              </span>
              <span className="text-xs text-rf-ink-soft">
                JPG, PNG or WEBP · up to 6 photos · tap or drag to upload
              </span>
            </button>
          )}
          <input ref={fileRef} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadImages(Array.from(e.target.files || []))} />
          {data.images?.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3">
              {data.images.map((url, i) => (
                <div key={i} className="relative aspect-square rounded-lg overflow-hidden group">
                  <img src={url} alt={`recipe-${i}`} className="w-full h-full object-cover" />
                  <button type="button" onClick={() => set("images", data.images.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 bg-rf-ink/80 text-rf-cream rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* AI headline image — SuperAdmin-only internal tool.
            Used to backfill hero images on imported / OCR'd recipes that
            arrive without a decent photo. Not surfaced to normal users. */}
        {(isSuperAdmin || isCopyOwner) && (
        <div className="bg-gradient-to-br from-rf-amber/20 to-rf-cream-alt rounded-3xl p-5 border border-rf-amber/40" data-testid="edit-ai-image-section">
          <div className="flex items-center gap-2 mb-2">
            <i className="fa-solid fa-wand-magic-sparkles text-rf-orange" aria-hidden="true" />
            <p className="overline !mb-0">AI headline image</p>
            <span className="ml-auto text-[10px] font-black uppercase tracking-wider bg-rf-orange text-white px-2 py-0.5 rounded-full">
              SuperAdmin
            </span>
          </div>
          <p className="text-sm text-rf-ink-soft mb-4">
            Generate a professional food-photography style headline image
            for this recipe. Unlimited regenerations — Keep, Replace or
            Delete each result before saving the recipe.
          </p>

          {!data.ai_image_url ? (
            <button
              type="button"
              data-testid="edit-generate-ai-image"
              onClick={() => generateAiImage()}
              disabled={generatingAiImage}
              className="btn-amber inline-flex items-center gap-2 disabled:opacity-50"
            >
              {generatingAiImage ? (
                <>
                  <div className="w-4 h-4 border-2 border-rf-ink border-t-transparent rounded-full animate-spin" />
                  Generating (30-60s)…
                </>
              ) : (
                <>
                  <i className="fa-solid fa-wand-magic-sparkles" aria-hidden="true" />
                  Generate AI headline image
                </>
              )}
            </button>
          ) : (
            <div>
              <div className="relative aspect-video rounded-2xl overflow-hidden mb-3 max-w-md">
                <img
                  src={data.ai_image_url}
                  alt="AI generated headline"
                  className="w-full h-full object-cover"
                  data-testid="edit-ai-image-preview"
                />
                {!data.ai_image_active && (
                  <div className="absolute inset-0 bg-rf-ink/40 flex items-center justify-center">
                    <span className="bg-rf-cream text-rf-ink text-xs font-black uppercase tracking-wider px-3 py-1.5 rounded-full">Hidden — Keep to use as main image</span>
                  </div>
                )}
              </div>

              {/* Keep / Replace / Delete controls */}
              <div className="flex flex-wrap gap-2 mb-3">
                <button
                  type="button"
                  data-testid="edit-ai-image-keep"
                  onClick={() => { set("ai_image_active", true); toast.success("Marked as main image — remember to Save."); }}
                  disabled={generatingAiImage || deletingAiImage || !!data.ai_image_active}
                  className="btn-amber inline-flex items-center gap-2 disabled:opacity-50"
                >
                  <i className="fa-solid fa-check" aria-hidden="true" />
                  {data.ai_image_active ? "Kept as main image" : "Keep"}
                </button>
                <button
                  type="button"
                  data-testid="edit-ai-image-replace"
                  onClick={() => generateAiImage()}
                  disabled={generatingAiImage || deletingAiImage}
                  className="btn-outline-ink inline-flex items-center gap-2 disabled:opacity-50"
                >
                  {generatingAiImage ? (
                    <>
                      <div className="w-4 h-4 border-2 border-rf-ink border-t-transparent rounded-full animate-spin" />
                      Regenerating…
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-rotate" aria-hidden="true" />
                      Replace
                    </>
                  )}
                </button>
                <button
                  type="button"
                  data-testid="edit-ai-image-delete"
                  onClick={deleteAiImage}
                  disabled={generatingAiImage || deletingAiImage}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-red-600/40 text-red-600 font-bold hover:bg-red-50 disabled:opacity-50"
                >
                  {deletingAiImage ? "Deleting…" : (<><Trash size={16} /> Delete</>)}
                </button>
              </div>

              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="edit-ai-image-toggle"
                  checked={!!data.ai_image_active}
                  onChange={(e) => set("ai_image_active", e.target.checked)}
                  className="w-5 h-5 accent-rf-orange"
                />
                <span className="text-sm font-bold text-rf-ink">
                  {data.ai_image_active
                    ? "Using AI image as the main image"
                    : "Use this AI image as the main image"}
                </span>
              </label>
              <p className="text-xs text-rf-ink-soft mt-2">
                <i className="fa-solid fa-circle-info mr-1" aria-hidden="true" />
                Save the recipe to persist your choice.
              </p>
            </div>
          )}
        </div>
        )}

        <div>
          <label className="overline">Experiment level</label>
          <input type="range" min="0" max="100" value={data.experiment_level} onChange={(e) => set("experiment_level", +e.target.value)} className="w-full accent-rf-orange mt-2" />
          <p className="text-xs text-rf-ink-soft">Structured ←──────→ Experimental</p>
        </div>

        <div>
          <label className="overline">Ingredients</label>
          <p className="text-xs text-rf-ink-soft mb-2">Drag the <span className="inline-block align-middle">⠿</span> handle on the left to reorder.</p>
          <SortableList
            items={data.ingredients}
            getId={(_, i) => ingIds[i] ?? `ing-fallback-${i}`}
            onReorder={(newIngs) => {
              // Reorder both the items AND their parallel ids in lockstep so
              // stable identities follow the row instead of the index.
              setData((d) => ({ ...d, ingredients: newIngs }));
              setIngIds((prev) => {
                const oldIdByRef = new Map(data.ingredients.map((it, i) => [it, prev[i]]));
                return newIngs.map((it) => oldIdByRef.get(it));
              });
            }}
            testidPrefix="edit-ingredient-row"
            renderItem={(ing, i) => (
              /* Mobile: name gets its own 12-col row, then Qty (4) + Unit (6) +
                 Delete (2) on the next row so both inputs are fully readable
                 while editing. Desktop (≥sm): revert to a single 6/2/2/2 row. */
              <div className="grid grid-cols-12 gap-2 mt-2 items-center">
                <input
                  value={ing.name}
                  onChange={(e) => updIng(i, "name", e.target.value)}
                  placeholder="Ingredient"
                  className="input-rf col-span-12 sm:col-span-6"
                />
                <input
                  type="number" inputMode="decimal" min="0" step="any"
                  value={ing.quantity ?? ""}
                  onChange={(e) => updIng(i, "quantity", e.target.value === "" ? null : Math.max(0, +e.target.value))}
                  onWheel={(e) => e.target.blur()}
                  placeholder="Qty"
                  className="input-rf col-span-4 sm:col-span-2"
                />
                <input
                  value={ing.unit}
                  onChange={(e) => updIng(i, "unit", e.target.value)}
                  placeholder="Unit"
                  className="input-rf col-span-6 sm:col-span-2"
                />
                <button
                  onClick={() => {
                    setData((d) => ({ ...d, ingredients: d.ingredients.filter((_, idx) => idx !== i) }));
                    setIngIds((prev) => prev.filter((_, idx) => idx !== i));
                  }}
                  aria-label="Remove ingredient"
                  className="col-span-2 min-h-[44px] rounded-lg text-rf-ink-soft hover:text-red-600 hover:bg-red-50 flex items-center justify-center transition-colors"
                >
                  <Trash size={18} />
                </button>
              </div>
            )}
          />
          <button
            onClick={() => {
              setData((d) => ({ ...d, ingredients: [...d.ingredients, { name: "", quantity: 0, unit: "", notes: "" }] }));
              setIngIds((prev) => [...prev, newRid("ing")]);
            }}
            className="text-rf-orange text-sm font-bold mt-2"
          >+ Add ingredient</button>
        </div>

        <div>
          <label className="overline">Instructions</label>
          <p className="text-xs text-rf-ink-soft mb-2">Drag the <span className="inline-block align-middle">⠿</span> handle on the left to reorder. Step numbers renumber automatically.</p>
          <SortableList
            items={data.instructions}
            getId={(_, i) => stepIds[i] ?? `step-fallback-${i}`}
            onReorder={(newSteps) => {
              setData((d) => ({ ...d, instructions: newSteps }));
              setStepIds((prev) => {
                // Instructions are strings — same value can occur twice, so
                // we can't map by value. Instead swap ids using the same move
                // shape as the caller (positions are what changed).
                const oldItems = data.instructions;
                const usedPrev = new Array(prev.length).fill(false);
                return newSteps.map((s) => {
                  for (let k = 0; k < oldItems.length; k++) {
                    if (!usedPrev[k] && oldItems[k] === s) {
                      usedPrev[k] = true;
                      return prev[k];
                    }
                  }
                  return newRid("step");
                });
              });
            }}
            testidPrefix="edit-instruction-row"
            renderItem={(s, i) => (
              <div className="flex gap-2 mt-2 items-start">
                <div className="w-9 h-9 rounded-full bg-rf-amber text-rf-ink flex items-center justify-center font-bold shrink-0">{i + 1}</div>
                <AutoGrowTextarea value={s} onChange={(e) => updStep(i, e.target.value)} className="input-rf flex-1 resize-none" />
                <button
                  onClick={() => {
                    setData((d) => ({ ...d, instructions: d.instructions.filter((_, idx) => idx !== i) }));
                    setStepIds((prev) => prev.filter((_, idx) => idx !== i));
                  }}
                  className="text-rf-ink-soft hover:text-red-600 p-2"
                ><Trash size={18} /></button>
              </div>
            )}
          />
          <button
            onClick={() => {
              setData((d) => ({ ...d, instructions: [...d.instructions, ""] }));
              setStepIds((prev) => [...prev, newRid("step")]);
            }}
            className="text-rf-orange text-sm font-bold mt-2"
          >+ Add step</button>
        </div>

        <div className="card-recipe bg-rf-cream-alt">
          <label className="flex items-start gap-3 cursor-pointer mb-3">
            <input data-testid="edit-public" type="checkbox" checked={data.is_public} disabled={uneditedCopyLocked && !data.is_public} onChange={(e) => set("is_public", e.target.checked)} className="mt-1 w-5 h-5 accent-rf-orange disabled:opacity-40" />
            <div>
              <p className="font-display font-bold text-rf-ink">Public</p>
              <p className="text-sm text-rf-ink-soft">List in directory.</p>
              {data.copied_from_recipe_id && !data.is_public && (
                <p className="text-xs text-rf-orange mt-1" data-testid="edit-public-copy-hint">
                  {uneditedCopyLocked ? "This is a copy — make a change first. " : ""}To share a copy publicly it needs its own new photo or AI image.
                </p>
              )}
            </div>
          </label>
          {data.is_public && (
            <label className="flex items-start gap-3 cursor-pointer pl-8">
              <input type="checkbox" checked={data.copyright_certified} onChange={(e) => set("copyright_certified", e.target.checked)} className="mt-1 w-5 h-5 accent-rf-orange" />
              <p className="text-sm text-rf-ink-soft">I own the rights to distribute this recipe publicly.</p>
            </label>
          )}
          <label className="flex items-start gap-3 cursor-pointer mt-3">
            <input data-testid="edit-secret" type="checkbox" checked={data.is_secret} onChange={(e) => set("is_secret", e.target.checked)} className="mt-1 w-5 h-5 accent-rf-orange" />
            <div>
              <p className="font-display font-bold text-rf-ink">Secret Recipe</p>
              <p className="text-sm text-rf-ink-soft">Hide from &ldquo;Minus Secrets&rdquo; shares.</p>
            </div>
          </label>
          {/* Owner opt-in: show the AI-drafted per-serving nutrition card
              on this recipe. Admin kill-switch takes priority — if the
              site flag is OFF the card is hidden regardless of this box.
              Toggling ON for the first time auto-estimates on next view. */}
          <label className="flex items-start gap-3 cursor-pointer mt-3">
            <input
              data-testid="edit-show-nutrition"
              type="checkbox"
              checked={data.show_nutrition === true}
              onChange={(e) => set("show_nutrition", e.target.checked)}
              className="mt-1 w-5 h-5 accent-rf-orange"
            />
            <div>
              <p className="font-display font-bold text-rf-ink">Show nutritional details</p>
              <p className="text-sm text-rf-ink-soft">Add a per-serving macro card at the end of the recipe. Figures are AI estimates with a &ldquo;not medical advice&rdquo; note — leave off to keep the page shorter.</p>
            </div>
          </label>
        </div>

        <div className="flex flex-wrap gap-3 justify-between pt-4">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                data-testid="edit-delete"
                type="button"
                className="text-red-600 font-bold inline-flex items-center gap-2 px-4 py-3 rounded-xl border-2 border-red-600/30 active:bg-red-50 hover:bg-red-50 min-h-[44px] touch-manipulation"
              >
                <Trash size={18} /> Delete
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="edit-delete-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>{hardDelete ? "Delete this recipe permanently?" : "Move this recipe to Trash?"}</AlertDialogTitle>
                <AlertDialogDescription>
                  {hardDelete
                    ? "This can't be undone. Any audio memories, scans and images tied to it will also be removed."
                    : "You can restore it from Trash on your dashboard for 30 days. After that it's deleted for good."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="edit-delete-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="edit-delete-confirm"
                  onClick={doDelete}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {hardDelete ? "Delete permanently" : "Move to Trash"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <div className="flex gap-3">
            <Link to={recipePath(data)} className="btn-outline-ink">Cancel</Link>
            <button data-testid="edit-save" onClick={save} className="btn-amber"><FloppyDisk size={18} /> Save changes</button>
          </div>
        </div>
      </div>
    </div>
  );
}
